import {
  type ICategory,
  type ICustomerCategory,
  type ICustomerProductDetail,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import { type ICategoryState } from 'customer/react/reducers/category/types';
import { type ILanguageState } from 'customer/react/reducers/language/types';
import { type IProductState } from 'customer/react/reducers/product/types';
import { type IProductsState } from 'customer/react/reducers/products/types';
import React, { type ReactElement, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { reset } from '../../reducers/product/reducer';
import { getCategoryUrl } from '../../utils/category';

function Breadcrumbs({
  Product,
  Language,
  Products,
  Category,
  dispatch,
}: {
  Product: IProductState;
  Language: ILanguageState;
  Products: IProductsState;
  Category: ICategoryState;
  dispatch: Dispatch;
}): React.JSX.Element {
  const getBreadcrumbElement = useCallback(
    (
      categories: ICategory[],
      category: ICustomerCategory,
    ): React.JSX.Element => {
      const c: ICategory | undefined = categories.find(
        (categoryItem: ICategory): boolean =>
          categoryItem.uuid === category.uuid,
      );

      const categoryName: ITranslation | undefined = c?.names.find(
        (name: ITranslation): boolean =>
          name.code === Language.languageType.code,
      );

      const categoryTo = c
        ? getCategoryUrl(c, Language.languageType.code)
        : `/${Language.languageType.code.toLowerCase()}/category/${category.uuid}`;

      return (
        <li>
          <Link
            to={categoryTo}
            className="breadcrumbs--category"
            onClick={(e: React.MouseEvent): void => {
              if (!e.shiftKey && !e.ctrlKey && !e.metaKey && e.button !== 1) {
                dispatch(reset());
              }
            }}
          >
            {categoryName ? categoryName.value : '...'}
          </Link>
        </li>
      );
    },
    [Language.languageType.code, dispatch],
  );

  const childCategoryElement: ReactElement | null =
    useMemo((): ReactElement | null => {
      const activeCategoryUUID: string = Products.category?.uuid;
      if (!activeCategoryUUID) {
        return null;
      }

      const parentCategory: ICategory | undefined = Category.categories.find(
        (category: ICategory): boolean =>
          category.childCategoriesUUIDs.includes(activeCategoryUUID),
      );

      if (!parentCategory) {
        return null;
      }

      return getBreadcrumbElement(Category.categories, parentCategory);
    }, [Category.categories, Products.category?.uuid, getBreadcrumbElement]);

  const productElement = useMemo((): ReactElement | null => {
    const product: ICustomerProductDetail | null = Product.product;

    if (!product || !product.categories || product.categories.length === 0) {
      return null;
    }

    const [category] = product.categories;

    if (!category) {
      return null;
    }

    return getBreadcrumbElement(Category.categories, category);
  }, [Product.product, Category.categories, getBreadcrumbElement]);

  return (
    <nav aria-label="Breadcrumb">
      <ol className="breadcrumbs">
        <li>
          <Link
            to={`/${Language.languageType.code.toLowerCase()}/home`}
            className="breadcrumbs--home"
            aria-label="Home"
            onClick={(e: React.MouseEvent): void => {
              if (!e.shiftKey && !e.ctrlKey && !e.metaKey && e.button !== 1) {
                dispatch(reset());
              }
            }}
          >
            <svg focusable="false" aria-hidden="true" viewBox="0 0 24 24">
              <path d="m12 5.69 5 4.5V18h-2v-6H9v6H7v-7.81l5-4.5M12 3 2 12h3v8h6v-6h2v6h6v-8h3L12 3z" />
            </svg>
          </Link>
        </li>
        {childCategoryElement}
        {productElement}
      </ol>
    </nav>
  );
}

const mapStateToProps = ({ Language, Product, Category, Products }) => ({
  Language,
  Product,
  Products,
  Category,
});

export default connect(mapStateToProps)(Breadcrumbs);
